import { useEffect, useRef, useState } from "react";
import { uploadImage, deleteImageByPath, pathFromPublicUrl } from "@/lib/storage";
import { UploadSimple, Trash, ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * ImageUpload — preview + upload/replace/remove.
 * Emits the resolved public URL via onChange when the upload completes.
 */
export default function ImageUpload({
  value,
  onChange,
  folder = "blogs",
  label = "Featured Image",
  testId = "image-upload",
}) {
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    setPreview(value || "");
  }, [value]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max size is 5 MB");
      return;
    }
    // Immediate local preview
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);

    setBusy(true);
    setProgress(20);
    try {
      const { url } = await uploadImage(file, folder);
      setProgress(100);
      onChange?.(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.message || "Upload failed");
      setPreview(value || "");
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(0), 800);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onRemove = async () => {
    const path = pathFromPublicUrl(value);
    if (path) await deleteImageByPath(path);
    setPreview("");
    onChange?.("");
    toast.success("Image removed");
  };

  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">
        {label}
      </label>
      <div
        data-testid={testId}
        className="mt-1.5 rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 p-4"
      >
        {preview ? (
          <div className="flex items-start gap-4">
            <img
              src={preview}
              alt="Preview"
              className="h-28 w-28 object-cover rounded-xl border border-slate-200 bg-white"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-slate-500 truncate">
                {value?.split("/").pop() || "Local preview"}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => inputRef.current?.click()}
                  data-testid={`${testId}-replace`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-navy-deep text-white text-[12px] font-semibold hover:bg-navy"
                >
                  <UploadSimple size={12} weight="bold" /> Replace
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={onRemove}
                  data-testid={`${testId}-remove`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white border border-red-200 text-red-600 text-[12px] font-semibold hover:bg-red-50"
                >
                  <Trash size={12} weight="bold" /> Remove
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId}-select`}
            className="w-full py-6 flex flex-col items-center gap-2 text-slate-500 hover:text-cyan-brand transition-colors"
          >
            <ImageSquare size={30} weight="thin" />
            <span className="text-sm font-medium">Click to upload image</span>
            <span className="text-[11px]">PNG, JPG, WEBP up to 5 MB</span>
          </button>
        )}
        {busy && progress > 0 && (
          <div className="mt-3 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-cta-gradient transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={onFile}
          className="hidden"
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
}
